"use client";

import { addCalendarDays } from "@/lib/calendarDate";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { 
  CheckCircle2, 
  Clock, 
  User, 
  Mail, 
  Phone, 
  FileText, 
  ShieldCheck, 
  ChevronRight,
  Sparkles,
  Heart,
  Info,
  CalendarIcon,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import WeeklyCalendar from "@/components/booking/WeeklyCalendar";
import Reveal from "@/components/motion/Reveal";
import ServiceHeroHeader from "@/components/Services/ServiceHeroHeader";
import HeaderPhoneButton from "@/components/Shared/HeaderPhoneButton";

type Slot = {
  id: number;
  startAt: number;
  endAt: number;
  isBooked?: boolean;
  isPast?: boolean;
};

function startOfWeek(date: Date) {
  const copy = new Date(date);
  const day = (copy.getDay() + 6) % 7;
  copy.setDate(copy.getDate() - day);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function formatDateInput(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

function getWeekNumber(date: Date) {
  const target = new Date(date);
  const day = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - day + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target.getTime() - firstThursday.getTime();
  return 1 + Math.round(diff / (7 * 24 * 60 * 60 * 1000));
}


function hasBookableSlot(weekSlots: Slot[]) {
  return weekSlots.some((slot) => !slot.isBooked && !slot.isPast);
}

export default function BookingClient() {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
    acceptRules: false,
    acceptGdpr: false,
    honeypot: "",
  });
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successCode, setSuccessCode] = useState<string | null>(null);
  const [weekFade, setWeekFade] = useState(false);
  const startedAtRef = useRef(Date.now());
  const formRef = useRef<HTMLFormElement>(null);
  const router = useRouter();
  
  const [activeDayKey, setActiveDayKey] = useState<string>(() =>
    formatDateInput(startOfWeek(new Date()))
  );

  const weekKey = useMemo(() => formatDateInput(weekStart), [weekStart]);
  const minWeek = useMemo(() => startOfWeek(new Date()), []);
  const maxWeek = useMemo(
    () => startOfWeek(addCalendarDays(new Date(), 28)),
    []
  );

  useEffect(() => {
    async function fetchWeekSlots(date: Date) {
      const tzOffset = date.getTimezoneOffset();
      const key = formatDateInput(date);
      const res = await fetch(`/api/bookings/logopedia/slots?weekStart=${key}&tzOffset=${tzOffset}`, {
        cache: "no-store",
      });
      if (!res.ok) {
        throw new Error("Nem sikerült betölteni az időpontokat.");
      }
      return (await res.json()) as Slot[];
    }

    let cancelled = false;
    let fadeTimer: ReturnType<typeof setTimeout> | null = null;

    async function loadSlots() {
      if (!hasLoaded) {
        setLoading(true);
      }
      setError(null);
      setWeekFade(true);

      try {
        let targetWeek = new Date(weekStart);
        let data = await fetchWeekSlots(targetWeek);

        while (!hasLoaded && !hasBookableSlot(data) && targetWeek.getTime() < maxWeek.getTime()) {
          targetWeek = addCalendarDays(targetWeek, 7);
          data = await fetchWeekSlots(targetWeek);
        }

        if (cancelled) {
          return;
        }

        setSlots(data);
        if (!hasLoaded && targetWeek.getTime() !== weekStart.getTime()) {
          setWeekStart(targetWeek);
          setActiveDayKey(formatDateInput(targetWeek));
        }
        setSelectedSlot((prev) => {
          if (!prev) return prev;
          const updated = data.find((slot) => slot.id === prev.id);
          if (!updated || updated.isBooked || updated.isPast) return null;
          return updated;
        });
        setLoading(false);
        setHasLoaded(true);
        fadeTimer = setTimeout(() => setWeekFade(false), 200);
      } catch {
        if (cancelled) {
          return;
        }
        setError("Nem sikerült betölteni az időpontokat.");
        setLoading(false);
        fadeTimer = setTimeout(() => setWeekFade(false), 200);
      }
    }

    void loadSlots();

    return () => {
      cancelled = true;
      if (fadeTimer) {
        clearTimeout(fadeTimer);
      }
    };
  }, [hasLoaded, maxWeek, weekStart]);

  const days = useMemo(() => {
    return Array.from({ length: 5 }, (_, index) => {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + index);
      return date;
    });
  }, [weekStart]);

  const slotsByDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    slots.forEach((slot) => {
      const date = new Date(slot.startAt);
      const key = formatDateInput(date);
      const list = map.get(key) ?? [];
      list.push(slot);
      map.set(key, list);
    });
    return map;
  }, [slots]);

  const dayData = useMemo(
    () =>
      days.map((day) => ({
        key: formatDateInput(day),
        date: day,
        shortLabel: day.toLocaleDateString("hu-HU", { weekday: "short" }),
        dateLabel: day.toLocaleDateString("hu-HU", { month: "short", day: "numeric" }),
        slots: slotsByDay.get(formatDateInput(day)) ?? [],
      })),
    [days, slotsByDay]
  );

  useEffect(() => {
    if (!dayData.find((day) => day.key === activeDayKey)) {
      setActiveDayKey(dayData[0]?.key ?? weekKey);
    }
  }, [dayData, activeDayKey, weekKey]);

  useEffect(() => {
    if (selectedSlot && formRef.current) {
        // Subtle scroll to form when slot selected
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedSlot]);

  useEffect(() => {
    if (!successCode) return;
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("logopediaBookingCode", successCode);
    }
    const target = "/logopedia-foglalas/thank-you";
    const timer = window.setTimeout(() => router.push(target), 300);
    return () => window.clearTimeout(timer);
  }, [router, successCode]);

  const weekLabel = `${getWeekNumber(weekStart)}. hét`;
  const dateRangeLabel = `${weekStart.toLocaleDateString("hu-HU")} - ${addCalendarDays(weekStart, 4).toLocaleDateString("hu-HU")}`;

  const selectedSlotLabel = useMemo(() => {
      if (!selectedSlot) return null;
      const date = new Date(selectedSlot.startAt);
      return date.toLocaleDateString("hu-HU", { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric', 
          weekday: 'long',
          hour: '2-digit',
          minute: '2-digit'
      });
  }, [selectedSlot]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!selectedSlot) {
      setError("Kérjük, válasszon egy időpontot a naptárban.");
      return;
    }
    if (form.honeypot.trim()) return;

    const elapsedMs = Date.now() - startedAtRef.current;
    if (elapsedMs < 3000) {
      setError("Kérjük, várjon néhány másodpercet a küldés előtt.");
      return;
    }
    setSaving(true);
    setError(null);

    fetch("/api/bookings/logopedia", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slotId: selectedSlot.id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: form.notes.trim() || null,
        acceptRules: form.acceptRules,
        acceptGdpr: form.acceptGdpr,
        honeypot: form.honeypot,
        clientElapsedMs: elapsedMs,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.ok) throw new Error(data.error || "Sikertelen foglalás.");
        setSuccessCode(data.code);
      })
      .catch((err) => {
        setError(err.message || "Sikertelen foglalás.");
      })
      .finally(() => {
        setSaving(false);
      });
  }

  return (
    <main className="min-h-screen bg-background text-foreground">
      <ServiceHeroHeader
        imageSrc="/images/booking/booking-hero.webp"
        imageAlt="Logopédia Foglalás"
        breadcrumbItems={[
          { label: "Főoldal", href: "/" },
          { label: "Logopédia", href: "/nyelvlokeses-nyeles-logopedia-gyor" },
          { label: "Időpontfoglalás" },
        ]}
        badge="Online Foglalás"
        title="Logopédiai Időpont Foglalása"
        description="Válassza ki az Önnek megfelelő időpontot modern naptárunkban, és rögzítse foglalását néhány egyszerű lépésben."
        actions={
            <div className="flex gap-4">
                <HeaderPhoneButton />
            </div>
        }
      />

      <article className="py-12 md:py-24">
        <div className="container mx-auto px-0 md:px-4">
          {successCode ? (
            <Reveal>
              <div className="max-w-2xl mx-auto rounded-3xl border-2 border-accent/20 bg-accent/5 p-8 md:p-12 text-center shadow-card">
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-6 text-accent-foreground shadow-lg">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h2 className="font-display text-3xl font-bold text-foreground mb-3">
                  Foglalás rögzítve
                </h2>
                <p className="text-muted-foreground text-lg">
                  Átirányítjuk Önt a visszaigazoló oldalra...
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="grid lg:grid-cols-3 gap-12">
              
              {/* Main Booking Flow */}
              <div className="lg:col-span-2 space-y-12">
                
                {/* Step 1: Calendar */}
                <section id="idopont" className="scroll-mt-24">
                  <Reveal>
                    <div className="flex items-center gap-4 mb-8 px-4 md:px-0">
                       <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">1</div>
                       <div>
                           <h2 className="text-2xl font-display font-bold">Válasszon Időpontot</h2>
                           <p className="text-sm text-muted-foreground">Kattintson az Önnek megfelelő szabad órára</p>
                       </div>
                    </div>
                    
                    <WeeklyCalendar
                      days={dayData}
                      weekLabel={weekLabel}
                      dateRangeLabel={dateRangeLabel}
                      loading={loading}
                      hasLoaded={hasLoaded}
                      weekFade={weekFade}
                      onPrevWeek={() => setWeekStart(addCalendarDays(weekStart, -7))}
                      onNextWeek={() => setWeekStart(addCalendarDays(weekStart, 7))}
                      prevDisabled={weekStart <= minWeek}
                      nextDisabled={weekStart >= maxWeek}
                      selectedSlotId={selectedSlot?.id ?? null}
                      onSelectSlot={(slot) => setSelectedSlot(slot)}
                      activeDayKey={activeDayKey}
                      onActiveDayChange={setActiveDayKey}
                    />
                  </Reveal>
                </section>

                {/* Step 2: Form */}
                <section id="adatok" ref={formRef} className="scroll-mt-24">
                  <Reveal delay={0.1}>
                    <div className="flex items-center gap-4 mb-8 px-4 md:px-0">
                       <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg transition-colors ${selectedSlot ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>2</div>
                       <div>
                           <h2 className={`text-2xl font-display font-bold transition-colors ${selectedSlot ? 'text-foreground' : 'text-muted-foreground'}`}>Adja meg Adatait</h2>
                           <p className="text-sm text-muted-foreground">A kiválasztott időpont véglegesítéséhez</p>
                       </div>
                    </div>

                    {!selectedSlot ? (
                        <div className="mx-4 md:mx-0 rounded-3xl border-2 border-dashed border-border p-12 text-center text-muted-foreground bg-muted/5">
                            <Clock className="w-10 h-10 mx-auto mb-4 opacity-20" />
                            <p className="font-medium italic">Kérjük, először válasszon ki egy szabad időpontot a fenti naptárban!</p>
                        </div>
                    ) : (
                        <div className="mx-4 md:mx-0 bg-card rounded-3xl border border-border shadow-card overflow-hidden">
                            <div className="bg-primary/5 p-6 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-accent/10 rounded-xl text-accent">
                                        <CalendarIcon className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Kiválasztott Időpont</p>
                                        <p className="font-display font-bold text-lg">{selectedSlotLabel}</p>
                                    </div>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => setSelectedSlot(null)} className="text-xs">
                                    Módosítás
                                </Button>
                            </div>

                            <form className="p-8 space-y-6" onSubmit={handleSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold flex items-center gap-2">
                                            <User className="w-4 h-4 text-primary" /> Név
                                        </label>
                                        <input
                                            className="w-full rounded-xl border border-border bg-background px-4 py-3 outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-xs"
                                            placeholder="Teljes név"
                                            value={form.name}
                                            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold flex items-center gap-2">
                                            <Mail className="w-4 h-4 text-primary" /> E-mail
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full rounded-xl border border-border bg-background px-4 py-3 outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-xs"
                                            placeholder="példa@email.hu"
                                            value={form.email}
                                            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-primary" /> Telefon
                                        </label>
                                        <input
                                            type="tel"
                                            className="w-full rounded-xl border border-border bg-background px-4 py-3 outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-xs"
                                            placeholder="+36 20 123 4567"
                                            value={form.phone}
                                            onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold flex items-center gap-2">
                                            <FileText className="w-4 h-4 text-primary" /> Megjegyzés (opcionális)
                                        </label>
                                        <input
                                            className="w-full rounded-xl border border-border bg-background px-4 py-3 outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-xs"
                                            placeholder="Bármi, amit fontosnak tart megosztani"
                                            value={form.notes}
                                            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                {/* Honeypot */}
                                <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden">
                                    <input type="text" value={form.honeypot} onChange={(e) => setForm(prev => ({ ...prev, honeypot: e.target.value }))} tabIndex={-1} autoComplete="off" />
                                </div>

                                <div className="space-y-4 pt-4 border-t border-border">
                                    <label className="flex items-start gap-4 cursor-pointer group">
                                        <div className="pt-0.5">
                                            <input
                                                type="checkbox"
                                                className="w-5 h-5 rounded-[0.25rem] border-border text-primary focus:ring-primary transition-all cursor-pointer accent-accent"
                                                checked={form.acceptRules}
                                                onChange={(e) => setForm((prev) => ({ ...prev, acceptRules: e.target.checked }))}
                                                required
                                            />
                                        </div>
                                        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                                            Elfogadom a <Link className="font-semibold text-primary underline" href="/booking-rules" target="_blank">Foglalási szabályzatot</Link>.
                                        </span>
                                    </label>

                                    <label className="flex items-start gap-4 cursor-pointer group">
                                        <div className="pt-0.5">
                                            <input
                                                type="checkbox"
                                                className="w-5 h-5 rounded-[0.25rem] border-border text-primary focus:ring-primary transition-all cursor-pointer accent-accent"
                                                checked={form.acceptGdpr}
                                                onChange={(e) => setForm((prev) => ({ ...prev, acceptGdpr: e.target.checked }))}
                                                required
                                            />
                                        </div>
                                        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                                            Elfogadom az <Link className="font-semibold text-primary underline" href="/gdpr" target="_blank">Adatkezelési tájékoztatót</Link>.
                                        </span>
                                    </label>
                                </div>

                                {error && (
                                    <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-xl text-sm flex items-center gap-3">
                                        <AlertCircle className="w-5 h-5 shrink-0" />
                                        {error}
                                    </div>
                                )}

                                <div className="pt-4">
                                    <Button
                                        type="submit"
                                        size="lg"
                                        className="w-full md:w-auto px-12 py-7 rounded-2xl bg-accent hover:bg-accent/90 text-accent-foreground font-black text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                                        disabled={saving}
                                    >
                                        {saving ? (
                                            <div className="flex items-center gap-3">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Foglalás...
                                            </div>
                                        ) : (
                                            "IDŐPONT VÉGLEGESÍTÉSE"
                                        )}
                                    </Button>
                                    <p className="mt-4 text-xs text-muted-foreground italic">
                                        A gombra kattintva Ön véglegesíti a foglalást. A visszaigazolást azonnal küldjük e-mailben.
                                    </p>
                                </div>
                            </form>
                        </div>
                    )}
                  </Reveal>
                </section>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-1 space-y-8 px-4 md:px-0">
                <Reveal direction="left" delay={0.2}>
                   <div className="bg-primary text-primary-foreground rounded-3xl p-8 shadow-card border border-primary-foreground/10 relative overflow-hidden group">
                        <div className="absolute -top-10 -right-10 opacity-10 group-hover:scale-110 transition-transform duration-700">
                            <Sparkles className="w-40 h-40 text-accent" />
                        </div>
                        <h3 className="text-xl font-display font-bold mb-6 flex items-center gap-2">
                            <Info className="w-5 h-5 text-accent" />
                            Tudnivalók
                        </h3>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-accent mt-0.5" />
                                <p className="text-sm opacity-90 leading-relaxed">A foglalás <span className="font-bold underline">legkésőbb 48 órával</span> az időpont előtt mondható le.</p>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-accent mt-0.5" />
                                <p className="text-sm opacity-90 leading-relaxed">Egyszerre <span className="font-bold underline">csak egy aktív időpont</span> választható ki a naptárban.</p>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-accent mt-0.5" />
                                <p className="text-sm opacity-90 leading-relaxed">Minden kezelésről <span className="font-bold underline">visszaigazoló e-mailt</span> küldünk Önnek.</p>
                            </li>
                        </ul>
                   </div>
                </Reveal>

                <Reveal direction="left" delay={0.3}>
                   <div className="bg-card rounded-3xl p-8 border border-border shadow-card">
                        <h3 className="text-xl font-display font-bold text-foreground mb-6 flex items-center gap-2">
                            <Heart className="w-5 h-5 text-accent" />
                            Segítségre van szüksége?
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                            Ha bizonytalan a választott kezelésben, vagy nem talál megfelelő időpontot, hívja recepciónkat bizalommal!
                        </p>
                        <div className="space-y-4">
                            <a href="tel:+36202972717" className="flex items-center gap-4 p-4 rounded-2xl bg-secondary/50 border border-border/50 hover:bg-secondary transition-colors group">
                                <div className="p-3 bg-accent text-accent-foreground rounded-xl shadow-xs group-hover:scale-110 transition-transform">
                                    <Phone className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Telefon</p>
                                    <p className="font-bold text-foreground">+36 20 297 2717</p>
                                </div>
                            </a>
                            <Link href="/kapcsolatfelvetel" className="flex items-center gap-2 text-primary font-bold text-sm hover:underline pl-2 transition-all group">
                                Írjon nekünk üzenetet <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                   </div>
                </Reveal>

              
              </aside>

            </div>
          )}
        </div>
      </article>
      
      {/* Bottom Legal / Helper info */}
      {!successCode && (
        <section className="pb-24">
            <div className="container mx-auto px-4">
                <Reveal>
                    <div className="max-w-4xl mx-auto bg-muted/20 border border-border rounded-2xl p-6 flex items-center gap-4 text-muted-foreground italic text-xs leading-relaxed">
                        <ShieldCheck className="w-10 h-10 shrink-0 opacity-50" />
                        <div>
                            Adatvédelmi tájékoztató: A megadott adatokat kizárólag a foglalás rögzítéséhez és kapcsolattartáshoz használjuk fel. Foglalásával Ön beleegyezik, hogy munkatársaink felvesszék Önnel a kapcsolatot a kezeléssel kapcsolatban. Adatkezelési nyilvántartási számunk: [Ide kerül az adatkezelési szám].
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
      )}
    </main>
  );
}
